import type { Tender } from '../types/tender';

export interface TenderStep {
  step: number;
  label: string;
  status: 'pending' | 'active' | 'completed';
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

/**
 * Calculates the current progress step (1-11) of a tender based on its
 * artifacts (files) and status.
 *
 * 11-Step Workflow:
 * 1. Tender Context
 * 2. Capability Analysis
 * 3. Upload Additional Docs
 * 4. Configuration
 * 5. Structure & Outline
 * 6. Draft Generation
 * 7. Review & Edit Proposal
 * 8. Create and Download DOCX
 * 9. Generate Documents & View Addenda
 * 10. Generate Additional CVs
 * 11. Final Draft Review
 */
export const calculateTenderStep = (tender: Tender): TenderStep => {
  // 1. Check for final stages
  if (tender.status === 'closed' || (tender.status as string) === 'submitted') {
    return { step: 12, label: 'Submitted', status: 'completed', color: 'success' };
  }

  // 2. If in Go/No-Go view (watching), override all subsequent draft steps
  const hasUserDocs = (tender.userDocsCount || 0) > 0;
  const status = (tender.status || '').toString().toLowerCase();
  const analysisStatus = (tender.alignmentAnalysis?.status || '').toLowerCase();
  const isAnalysisComplete =
    analysisStatus.includes('complete') || ['error', 'failed', 'n/a'].includes(analysisStatus);
  const isAnalysisProcessing =
    !isAnalysisComplete &&
    (status === 'processing initial docs' ||
      status === 'preparing documents' ||
      analysisStatus.length > 0);

  if (tender.pursuitStatus === 'watching') {
    if (tender.noFiles && !hasUserDocs) {
      return { step: 1, label: 'RETRIEVE FILES', status: 'active', color: 'error' };
    }
    if (isAnalysisProcessing) {
      return { step: 2, label: 'Wait for analysis', status: 'active', color: 'warning' };
    }
    return { step: 3, label: 'Go/No-Go Decision', status: 'active', color: 'primary' };
  }

  // 3. Check for Final Review Artifacts (Step 11 Done -> Step 12 Active or Step 11 iterating)
  if (tender.reviewFileLink) {
    return { step: 11, label: 'Final Review', status: 'active', color: 'warning' };
  }

  // 3. Check for Compiled DOCX (Step 8 Done -> Step 9/10/11 Active)
  if (tender.compiledFileLink) {
    return { step: 9, label: 'Finalise & Addenda', status: 'active', color: 'info' };
  }

  // 4. Check for Draft Artifacts (Step 6 Done -> Step 7 Active)
  const draftStatus = tender.draftResponse?.status?.toLowerCase();
  if (
    tender.draftFileLink ||
    draftStatus === 'sections generated' ||
    draftStatus === 'complete' ||
    draftStatus === 'success' ||
    draftStatus === 'draft complete'
  ) {
    return { step: 7, label: 'Review & Edit', status: 'active', color: 'info' };
  }

  // 5. Check for Structure Confirmation (Step 5 Done -> Step 6 Active)
  if (tender.structureConfirmed) {
    return { step: 6, label: 'Generate Draft', status: 'active', color: 'info' };
  }

  // 6. Check for Settings Confirmation (Step 4 Done -> Step 5 Active)
  if (tender.quickSettings?.enabled) {
    return { step: 5, label: 'Confirm structure', status: 'active', color: 'secondary' };
  }

  // 7. Check for Add Docs Confirmation (Step 3 Done -> Step 4 Active)
  if (tender.supportDocsCompleted) {
    return { step: 4, label: 'Update settings', status: 'active', color: 'primary' };
  }

  // If pursuing, handle missing files and isAnalysisProcessing only if higher steps haven't overridden
  if (tender.noFiles && !hasUserDocs) {
    return { step: 1, label: 'Retrieve Files', status: 'active', color: 'error' };
  }

  if (isAnalysisProcessing) {
    return { step: 2, label: 'Wait for analysis', status: 'active', color: 'warning' };
  }

  // 8. Check for Analysis Artifacts (Step 2 Done -> Step 3 Active)
  if (tender.analysisFileLink) {
    return { step: 3, label: 'Review Analysis', status: 'active', color: 'default' };
  }

  // 10. Default: Tender Context (Step 1)
  return { step: 1, label: 'Upload Documents', status: 'active', color: 'default' };
};

/**
 * Returns the percentage (0-100) for a linear progress bar.
 */
export const getStepProgressValue = (step: number): number => {
  return Math.round((step / 11) * 100);
};
